import React, { useEffect } from "react";
import { HexColorPicker } from "react-colorful";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "./ServiceModal.module.css";

/*
 * Bookable service lengths: 5, 15, 30, 45 min, every 15 min to 2 h, then every 30 min to 8 h.
 *
 * It used to stop at 2 h, which cannot describe a consultant's list — a general anaesthetic
 * operation runs anywhere from 1 to 8 hours (KIN-1986). Past two hours the step widens to 30 min:
 * quarter-hour precision on a half-day theatre slot is not information anyone has, and it would
 * turn this into a 33-item dropdown to scroll.
 */
const DURATION_MINUTES = [
  5,
  15,
  30,
  45,
  ...Array.from({ length: 5 }, (_, index) => 60 + index * 15), // 1 h → 2 h
  ...Array.from({ length: 12 }, (_, index) => 150 + index * 30), // 2 h 30 → 8 h
];

const formatDurationLabel = (minutes) => {
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  return remainder === 0 ? `${hours} h` : `${hours} h ${remainder} min`;
};

const DURATION_OPTIONS = DURATION_MINUTES.map((minutes) => ({
  value: String(minutes),
  label: formatDurationLabel(minutes),
}));

const PRESET_COLORS = [
  "#006546",
  "#2563eb",
  "#dc2626",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#7c3aed",
  "#db2777",
  "#0891b2",
  "#65a30d",
  "#f59e0b",
  "#84cc16",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#6366f1",
  "#f43f5e",
];

const toNumber = (errorMessage) =>
  z.preprocess(
    (val) => (val === "" || val == null ? undefined : Number(val)),
    z
      .number({
        required_error: errorMessage,
        invalid_type_error: errorMessage,
      })
      .min(0, errorMessage)
  );

const serviceSchema = z.object({
  name: z.string().min(1, "Please enter name."),
  duration: toNumber("Please enter duration.").refine(
    (val) => val >= 1,
    "Please enter duration."
  ),
  price: toNumber("Please enter price."),
  color: z.string().default("#006546"),
  vat: z.boolean().default(false),
  vatRate: z.union([z.string(), z.number()]).optional(),
});

const DEFAULT_VALUES = {
  name: "",
  duration: "",
  price: "",
  color: "#006546",
  vat: false,
  vatRate: "",
};

const SelectedColorPreview = ({ value }) => (
  <div className={styles.selectedColorPreview}>
    <div
      className={styles.selectedColorSwatch}
      style={{ backgroundColor: value }}
    />
    <span className={styles.selectedColorHex}>{value}</span>
  </div>
);

const ColorPicker = ({ value, onChange }) => (
  <div className={styles.colorPickerWrap}>
    <div className={styles.colorDotGrid}>
      {PRESET_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          className={`${styles.colorDot} ${
            value === color ? styles.colorDotSelected : ""
          }`}
          style={{ backgroundColor: color }}
          onClick={() => onChange(color)}
          title={color}
        />
      ))}
    </div>
    <div className={styles.hexPickerWrap}>
      <HexColorPicker color={value} onChange={onChange} />
    </div>
    <SelectedColorPreview value={value} />
  </div>
);

export const ServiceModal = ({
  isOpen,
  onClose,
  onSubmit,
  editService,
  error,
  setError,
}) => {
  const methods = useForm({
    resolver: zodResolver(serviceSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { control, handleSubmit, reset } = methods;

  useEffect(() => {
    if (!isOpen) return;

    reset(
      editService
        ? {
            name: editService.name,
            duration: editService.duration ?? "",
            price: editService.price ?? "",
            color: editService.color || "#006546",
            vat: editService.vat || false,
            vatRate: editService.vatRate || "",
          }
        : DEFAULT_VALUES
    );
    setError?.(null);
  }, [isOpen, editService, reset, setError]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={editService ? "Edit Service" : "Add Service"}
      width={720}
    >
      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit, onInvalidSubmit("ServiceModal"))}>
          {!!error && <div className={styles.errorBanner}>{error}</div>}

          <div className={styles.formGrid}>
            <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
              <label className={styles.label}>Services Name</label>
              <FormField name="name">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Service Name"
                      className={styles.input}
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Duration</label>
              <FormField name="duration">
                <Controller
                  name="duration"
                  control={control}
                  render={({ field }) => (
                    <Select
                      name="duration"
                      options={DURATION_OPTIONS}
                      value={
                        field.value === "" || field.value == null
                          ? ""
                          : String(field.value)
                      }
                      onChange={(value) => field.onChange(Number(value))}
                      placeholder="Select duration"
                      fullWidth
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Price</label>
              <FormField name="price">
                <div className={styles.euroInput}>
                  <Controller
                    name="price"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="number"
                        className={styles.input}
                      />
                    )}
                  />
                </div>
              </FormField>
            </div>

            <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
              <label className={styles.label}>Service Color</label>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <ColorPicker value={field.value} onChange={field.onChange} />
                )}
              />
            </div>
          </div>

          <div className={styles.footer}>
            <Button
              variant="outline"
              size="medium"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button variant="primary" size="medium" type="submit">
              Save
            </Button>
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
